import {
  getHoursFromTimeString,
  getMinutesFromTimeString,
  weekDays,
} from "@infrastructure/Utils";
import { Text } from "react-native-paper";
import { theme } from "@config/Theme";
import { StyleSheet, View } from "react-native";
import { ScheduleViewModel } from "@viewModels/Schedule.viewModel";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

interface Schedule {
  dayOfWeek: number;
  open: string;
  close: string;
}

interface Props {
  schedule?: Array<ScheduleViewModel> | null;
  forDetails?: boolean;
}

// Note:
// FROM BE side we get dayOfWeek in the response of each object
// dayOfWeek = 0 --> it refres to monday
// date.getDay() ---> 0 (Sunday) to 6 (Saturday)
function getScheduleForSpecificDate(
  schedules: Schedule[],
  date: Date
): Schedule | null {
  const day = date.getDay();
  const year = date.getFullYear();
  const month = date.getMonth();
  const filteredSchedules = schedules.filter((s) => s.dayOfWeek + 1 === day);

  if (filteredSchedules.length > 0) {
    const result = filteredSchedules.find((it) => {
      const dates = getOpeningAndClosingDate(it);
      dates.openingDate.setFullYear(year, month);
      dates.closingDate.setFullYear(year, month);
      if (date >= dates.openingDate && date <= dates.closingDate) {
        return it;
      }
    });

    if (result) {
      return result;
    } else {
      return findClosestSchedule(filteredSchedules);
    }
  } else {
    return null;
  }
}

function getOpeningAndClosingDate(schedule: Schedule) {
  const openingHours = getHoursFromTimeString(schedule.open);
  const openingMinutes = getMinutesFromTimeString(schedule.open);
  const closingingHours = getHoursFromTimeString(schedule.close);
  const closingMinutes = getMinutesFromTimeString(schedule.close);
  const openingDate = new Date();
  const closingDate = new Date();

  openingDate.setHours(openingHours);
  openingDate.setMinutes(openingMinutes);

  closingDate.setHours(closingingHours);
  closingDate.setMinutes(closingMinutes);

  return {
    openingDate: openingDate,
    closingDate: closingDate,
  };
}
/**
 * - in case we are in a specific day and this day has schedules
 * - but the current date is not in the schedule time range
 */
function findClosestSchedule(schedules: Schedule[]) {
  let closestSchedule = schedules[0];

  for (let index = 0; index < schedules.length; index++) {
    const date = getOpeningAndClosingDate(schedules[index]).openingDate;
    const date1 = getOpeningAndClosingDate(closestSchedule).openingDate;
    closestSchedule = date < date1 ? schedules[index] : closestSchedule;
  }

  return closestSchedule;
}

/**
 * - in case we are in a specific day and this day has no available schedules
 * - we will check other days
 */
function findNextScedule(schedules: Schedule[]) {
  let nextDayOffset = 1;
  let nextAvailableSchedule: Schedule | null = null;
  const currentDate = new Date();
  while (nextAvailableSchedule === null) {
    const day = (currentDate.getDay() + nextDayOffset) % 7;
    const filteredSchedules = schedules.filter((s) => s.dayOfWeek + 1 === day);
    if (filteredSchedules.length > 0) {
      nextAvailableSchedule = findClosestSchedule(filteredSchedules);
    } else {
      nextDayOffset++;
    }
  }

  return nextAvailableSchedule;
}

export const TodaySchedule = ({ schedule, forDetails = false }: Props) => {
  const { t } = useTranslation("common");
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [text, setText] = useState<string>("");

  if (!schedule || schedule.length === 0) {
    return null;
  }

  useEffect(() => {
    const currentDate = new Date();
    let scheduleAsResult: Schedule | null = null;
    scheduleAsResult = getScheduleForSpecificDate(schedule, currentDate);
    if (scheduleAsResult === null) {
      scheduleAsResult = findNextScedule(schedule);
    }

    const dayAsString = t(weekDays[scheduleAsResult.dayOfWeek]);
    const openingTime = scheduleAsResult.open;

    if (currentDate.getDay() === scheduleAsResult.dayOfWeek + 1) {
      const dates = getOpeningAndClosingDate(scheduleAsResult);
      const isOpen =
        currentDate >= dates.openingDate && currentDate <= dates.closingDate;
      const text = isOpen
        ? ""
        : t("kp_is_closed_will_open_at", {
            day: dayAsString,
            openingTime: openingTime,
          });

      setText(text);
      setIsOpened(isOpen);
    } else {
      const text = t("kp_is_closed_will_open_at", {
        day: dayAsString,
        openingTime: openingTime,
      });
      setText(text);
      setIsOpened(false);
    }
  }, []);

  const fontSize = forDetails ? 16 : 12;
  return (
    <View style={styles.container}>
      <Text style={[{ fontSize }, isOpened ? styles.open : styles.close]}>
        {isOpened ? t("todaySchedule_opened") : t("todaySchedule_closed")}
      </Text>
      <Text>-</Text>
      <Text style={{ fontSize, flex: 1 }}>{text}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 5,
  },
  open: {
    color: theme.colors.success,
  },
  close: {
    color: theme.colors.error,
  },
});
